import React from "react";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { FrontQuestionFormCard } from "../../components/Front/Complex/FrontQuestionFormCard/FrontQuestionFormCard";
import { FrontUserIdentityVerificationSummary } from "../../components/Front/Complex/FrontUserIdentityVerificationSummary/FrontUserIdentityVerificationSummary";
import FrontParagraphSmall from "../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { ClientCaseIdentityVerificationUserForm, TClientCaseIdentityVerificationUserForm } from "../../../../domain/codecs/form/ClientCaseForm";
import { FrontSpacing } from "../../models/FrontSpacing";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { HorizontalLine } from "../../components/BuildingBlocks/HorizontalLine";
import { FrontColors } from "../../models/FrontColors";
import { MaxWidth } from "../../components/BuildingBlocks/MaxWidth";
import { FrontFormSectionTitle } from "../../components/Front/Simple/FrontFormSectionTitle/FrontFormSectionTitle";
import { TOnboardingTabStatus } from "../../components/Front/Simple/FrontOnboardingTab/FrontOnboardingTab";
import { FrontIdentityVerificationRetryPopup } from "../../components/Front/Complex/FrontIdentityVerificationRetryPopup/FrontIdentityVerificationRetryPopup";
import { TUserCaseUserType } from "../../../../domain/codecs/UserCaseUserType";
import { getIdentityVerificationStepStatus, hasOutstandingIdentityVerificationForOthers } from "../../functions/onboarding/getIdentityVerificationStepStatus";


const getIdentityVerificationUsers = (props: TContainerStateProps): Array<TClientCaseIdentityVerificationUserForm> =>
    props.state.forms.client_case_page.children.identity_verification?.users || [];

const getSessionUserCaseUserType = (props: TContainerStateProps): TUserCaseUserType =>
    props.state.forms.client_case_page.children.session_user_case_user_type.user_type;

const FrontIdentityVerificationContainer = (props: TContainerStateProps): JSX.Element => {

    const getClients = (): Array<TClientCaseIdentityVerificationUserForm> =>
        getIdentityVerificationUsers(props);

    return (
        <>
            <div>
                <FrontQuestionFormCard
                    title="Verify your identity"
                    paragraph={
                        <MaxWidth width="600px">
                            <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                                <FrontParagraphSmall>
                                    Sail Legal verifies the <WeightBold>authenticity</WeightBold> of all <WeightBold>emails</WeightBold> and <WeightBold>phone numbers</WeightBold> our clients give us.
                                    This helps us be certain that we are actually talking to you, and protects you from fraud. 
                                </FrontParagraphSmall>
                                <FrontParagraphSmall>
                                    Here you can find the <WeightBold>verification status</WeightBold> of your contact 
                                    information, and if something is wrong, what you can do about it.
                                </FrontParagraphSmall>
                            </SpacingColumn>
                        </MaxWidth>
                    }
                >
                    <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                        {getClients().map((form, index) => (
                            <MaxWidth
                                key={index} 
                                width="400px"
                            >
                                <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                                    <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                                        {/* USE NAME */}
                                        <FrontFormSectionTitle>
                                            {form.children.user14.full_name}
                                        </FrontFormSectionTitle>

                                        {/* ID VERIFICATION SUMMARY */}
                                        <FrontUserIdentityVerificationSummary
                                            key={index}
                                            user={form}
                                            onChange={(payload) => props.dispatch({
                                                type: "CLIENT_UPDATE_ID_VERIFICATION_DETAILS",
                                                payload
                                            })}
                                            onAutoCompleteAddress={(payload) => props.dispatch({
                                                type: "CLIENT_AUTOCOMPLETE_ADDRESS",
                                                payload
                                            })}
                                            onSubmit={(payload) => props.dispatch({
                                                type: "CLIENT_SUBMIT_ID_VERIFICATION_DETAILS",
                                                payload
                                            })}
                                            onBeginPhotoVerification={(payload) => props.dispatch({
                                                type: "CLIENT_BEGIN_STRIPE_VERIFICATION",
                                                payload
                                            })}
                                            onProofOfAddressDocumentUpload={(payload) => props.dispatch({
                                                type: "CLIENT_PROOF_OF_ADDRESS_DOCUMENT_UPLOAD",
                                                payload,
                                            })}
                                        />
                                    </SpacingColumn>

                                    {/* SEPERATOR LINE */}
                                    {index < getClients().length-1 &&
                                        <HorizontalLine color={FrontColors.NEUTRAL_11} />
                                    }
                                </SpacingColumn>
                            </MaxWidth>
                        ))}
                    </SpacingColumn>
                </FrontQuestionFormCard>
            </div>

            {/* PHOTO VERIFICATION RETRY */}
            {props.state.routes.queryParams.VIEW_CLIENT_CASE.errorCode && props.state.routes.queryParams.VIEW_CLIENT_CASE.userCaseId && <FrontIdentityVerificationRetryPopup
                form={getClients().find((user) => user.edited.users__cases_id === props.state.routes.queryParams.VIEW_CLIENT_CASE.userCaseId) || ClientCaseIdentityVerificationUserForm.newDefault()}
                errorCode={props.state.routes.queryParams.VIEW_CLIENT_CASE.errorCode}
                onBeginPhotoVerification={(payload) => props.dispatch({
                    type: "CLIENT_BEGIN_STRIPE_VERIFICATION",
                    payload
                })}
            />}
        </>
    );
};

export const getIdentityVerificationStatus = (props: TContainerStateProps): TOnboardingTabStatus =>
    getIdentityVerificationStepStatus(
        getIdentityVerificationUsers(props),
        props.state.global.user.id,
        getSessionUserCaseUserType(props),
    );

export const doesIdentityVerificationCarryOthersOutstandingWork = (props: TContainerStateProps): boolean =>
    hasOutstandingIdentityVerificationForOthers(
        getIdentityVerificationUsers(props),
        props.state.global.user.id,
        getSessionUserCaseUserType(props),
    );

export default FrontIdentityVerificationContainer;
